import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "../../../lib/apiClient";
import { resolveBookId } from "../../../lib/bookLabRoutes";

const BookEditorContext = createContext(null);

/**
 * Shared Book Lab editor state for Write / Design / Canvas / Preview.
 * Turn 1: loads book + pages only (document endpoint lands in Turn 2).
 */
export function BookEditorProvider({ bookId, initialMode = "write", children }) {
  const resolvedId = resolveBookId(bookId) || bookId;

  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [document, setDocument] = useState(null);
  const [pages, setPages] = useState([]);
  const [assets, setAssets] = useState([]);
  const [sources, setSources] = useState([]);

  const [mode, setMode] = useState(initialMode);
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [activePageId, setActivePageId] = useState(null);

  const [selection, setSelection] = useState({ type: null, ids: [] });

  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(1);

  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [preflightIssues, setPreflightIssues] = useState([]);

  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightInspectorOpen, setRightInspectorOpen] = useState(true);
  const [toolSkillMode, setToolSkillMode] = useState("beginner");

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const setModeAndUrl = useCallback((nextMode) => {
    setMode(nextMode);
    try {
      const url = new URL(window.location.href);
      if (resolvedId) url.searchParams.set("bookId", resolvedId);
      url.searchParams.set("mode", nextMode);
      url.searchParams.delete("view");
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    } catch (_) { /* ignore */ }
  }, [resolvedId]);

  const loadEditor = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    try {
      const [bookResponse, pagesResponse] = await Promise.all([
        api.get(`/api/book-lab/books/${resolvedId}`),
        api.get(`/api/book-lab/books/${resolvedId}/pages`).catch(() => ({ items: [] })),
      ]);

      setBook(bookResponse);
      setChapters(bookResponse.chapters || []);
      setPages(pagesResponse.items || []);
      setRevision(bookResponse.revision || pagesResponse.revision || 1);

      setActiveChapterId((current) => (
        current && (bookResponse.chapters || []).some((c) => c.id === current)
          ? current
          : bookResponse.chapters?.[0]?.id || null
      ));
      setActivePageId((current) => (
        current && (pagesResponse.items || []).some((p) => p.id === current)
          ? current
          : pagesResponse.items?.[0]?.id || null
      ));

      // Document endpoint is Turn 2 — soft-load without failing the editor.
      try {
        const doc = await api.get(`/api/book-lab/books/${resolvedId}/document`);
        setDocument(doc);
        if (doc?.revision) setRevision(doc.revision);
      } catch (err) {
        if (err?.status && err.status !== 404) {
          // Non-404 means the route exists but failed — surface honestly later.
          setDocument(null);
        } else {
          setDocument(null);
        }
      }

      try {
        const assetsRes = await api.get(`/api/book-lab/books/${resolvedId}/assets`);
        setAssets(assetsRes.items || []);
      } catch (_) {
        setAssets([]);
      }

      try {
        const sourcesRes = await api.get(`/api/book-lab/books/${resolvedId}/sources`);
        setSources(sourcesRes.items || []);
      } catch (_) {
        setSources([]);
      }
    } catch (error) {
      setLoadError(error.message || "Unable to load the book editor.");
    } finally {
      setLoading(false);
    }
  }, [resolvedId]);

  useEffect(() => {
    loadEditor();
  }, [loadEditor]);

  const selectObject = useCallback((id, options = {}) => {
    setSelection((current) => {
      if (options.additive) {
        const exists = current.ids.includes(id);
        return {
          type: "object",
          ids: exists ? current.ids.filter((value) => value !== id) : [...current.ids, id],
        };
      }
      return { type: "object", ids: [id] };
    });
  }, []);

  const selectBlock = useCallback((id, options = {}) => {
    setSelection((current) => {
      if (options.additive) {
        const exists = current.ids.includes(id);
        return {
          type: "block",
          ids: exists ? current.ids.filter((value) => value !== id) : [...current.ids, id],
        };
      }
      return { type: "block", ids: [id] };
    });
  }, []);

  const selectObjects = useCallback((ids) => {
    setSelection({ type: "object", ids: ids || [] });
  }, []);

  const clearSelection = useCallback(() => {
    setSelection({ type: null, ids: [] });
  }, []);

  const executeCommand = useCallback(async (command) => {
    // Turn 4 wires the command API. Optimistic surface only for now.
    setSaveStatus("saving");
    setSaveError(null);
    try {
      const response = await api.post(`/api/book-lab/books/${resolvedId}/commands`, {
        ...command,
        revision,
      });
      setRevision(response.revision ?? revision + 1);
      if (response.document) setDocument(response.document);
      if (response.pages) setPages(response.pages);
      setUndoStack((stack) => [...stack, response.command || command]);
      setRedoStack([]);
      setSaveStatus("saved");
      window.setTimeout(() => {
        setSaveStatus((current) => (current === "saved" ? "idle" : current));
      }, 1400);
      return response;
    } catch (error) {
      setSaveStatus("failed");
      if (error?.status === 404) {
        setSaveError("Command API is not available yet (Turn 4). Local edits still use existing save paths.");
      } else if (error?.status === 409) {
        setSaveError(error.message || "Revision conflict — reloading.");
        await loadEditor();
      } else {
        setSaveError(error.message || "Save failed.");
      }
      throw error;
    }
  }, [resolvedId, revision, loadEditor]);

  const runPreflight = useCallback(async () => {
    const response = await api.post(`/api/book-lab/books/${resolvedId}/preflight`, { revision });
    setPreflightIssues(response.issues || []);
    return response;
  }, [resolvedId, revision]);

  const activeChapter = useMemo(
    () => chapters.find((chapter) => chapter.id === activeChapterId) || null,
    [chapters, activeChapterId]
  );

  const activePage = useMemo(
    () => pages.find((page) => page.id === activePageId) || null,
    [pages, activePageId]
  );

  const value = useMemo(() => ({
    bookId: resolvedId,
    book,
    chapters,
    setChapters,
    activeChapter,
    activeChapterId,
    setActiveChapterId,

    document,
    setDocument,
    bookStyles: document?.style_definitions || book?.style_tokens || null,
    documentSettings: document
      ? {
          trim: document.trim,
          margins: document.margins,
          bleed: document.bleed,
          facing_pages: document.facing_pages,
          start_first_page_on_right: document.start_first_page_on_right,
        }
      : null,

    pages,
    setPages,
    activePage,
    activePageId,
    setActivePageId,

    mode,
    setMode: setModeAndUrl,

    selection,
    selectObject,
    selectObjects,
    selectBlock,
    clearSelection,

    assets,
    sources,

    revision,
    executeCommand,
    undoStack,
    redoStack,
    undo: async () => {
      setSaveError("Undo arrives with the command system (Turn 4).");
    },
    redo: async () => {
      setSaveError("Redo arrives with the command system (Turn 4).");
    },

    saveStatus,
    saveError,
    loadError,
    loading,
    retrySave: loadEditor,

    preflightIssues,
    runPreflight,

    leftPanelOpen,
    setLeftPanelOpen,
    rightInspectorOpen,
    setRightInspectorOpen,
    toolSkillMode,
    setToolSkillMode,

    reload: loadEditor,
  }), [
    resolvedId, book, chapters, activeChapter, activeChapterId,
    document, pages, activePage, activePageId, mode, setModeAndUrl, selection,
    selectObject, selectObjects, selectBlock, clearSelection,
    assets, sources, revision, executeCommand, undoStack, redoStack,
    saveStatus, saveError, loadError, loading, loadEditor,
    preflightIssues, runPreflight,
    leftPanelOpen, rightInspectorOpen, toolSkillMode,
  ]);

  return (
    <BookEditorContext.Provider value={value}>
      {children}
    </BookEditorContext.Provider>
  );
}

export function useBookEditor() {
  const context = useContext(BookEditorContext);
  if (!context) {
    throw new Error("useBookEditor must be used inside BookEditorProvider.");
  }
  return context;
}
